import { CID } from 'multiformats/cid';

import { createAppAsyncThunk } from '@/store/thunks/createAppAsyncThunk';
import { addFileMetadata } from '@/store/slices/poc/heliaSlice';
import type { FileMetadata, ProgressCallback } from '@/types/poc/helia';
import { createFileMetadata } from '@/utils/file.utils';

const DEFAULT_MAX_WAIT_TIME = 300000; // 5 minutes
const PROGRESS_COMPLETE = 100;
const PROGRESS_START = 0;

export interface DownloadNetworkFileParams {
  sixId: string;
  cid: string;
  metadata?: FileMetadata;
  onProgress?: ProgressCallback;
  onChunkReceived?: (bytesLoaded: number) => void;
  signal?: AbortSignal;
  maxWaitTime?: number;
}

/**
 * Download a file from the network via Bitswap.
 *
 * fs.cat() triggers Bitswap to fetch missing blocks on-demand as it iterates
 * through the DAG. No polling loop is needed — Bitswap resolves each block as
 * the iterator requests it. An AbortSignal provides timeout protection.
 */
export const downloadFileFromNetwork = createAppAsyncThunk(
  'helia/downloadFromNetwork',
  async(
    {
      sixId,
      cid,
      metadata,
      onProgress,
      onChunkReceived,
      signal: externalSignal,
      maxWaitTime = DEFAULT_MAX_WAIT_TIME,
    }: DownloadNetworkFileParams,
    { extra: { pocServices, loggerService }, dispatch }
  ) => {
    const log = loggerService.createLogger({ namespace: 'six3ro:p2p:files:network' });
    log.info('Downloading file from network, CID:', cid);
    const { p2pStackManagerService, fileStoreService } = pocServices;
    const helia = p2pStackManagerService.getHeliaNode(sixId);

    if (helia == null) {
      throw new Error('Helia node not initialized');
    }

    const parsedCid = CID.parse(cid);

    // Check if file is already fully local (pinned = all DAG blocks present)
    const isPinned = await helia.helia.pins.isPinned(parsedCid);

    if (isPinned) {
      log.debug('File already pinned locally, reading from blockstore');
      const bytes = await fileStoreService.getFile(helia, cid);
      const fileMetadata = createFileMetadata({ cid, size: bytes.length, sixId, providedMetadata: metadata });

      dispatch(addFileMetadata(fileMetadata));

      if (onProgress !== undefined) onProgress(PROGRESS_COMPLETE, PROGRESS_COMPLETE);

      return {
        cid,
        metadata: fileMetadata,
        bytesDownloaded: bytes.length,
        source: 'local' as const
      };
    }

    // File not local — fetch via Bitswap with timeout protection.
    if (onProgress !== undefined) onProgress(PROGRESS_START, PROGRESS_COMPLETE);
    log.debug('File not pinned locally, fetching via Bitswap...');
    pocServices.cidDownloadLogger.log(cid, 'requesting', { expectedSize: metadata?.size });

    const timeoutSignal = AbortSignal.timeout(maxWaitTime);
    const signal = externalSignal != null
      ? AbortSignal.any([externalSignal, timeoutSignal])
      : timeoutSignal;

    try {
      const bytes = await fileStoreService.getFile(helia, cid, {
        signal,
        expectedSize: metadata?.size,
        onProgress: onChunkReceived != null
          ? (loaded) => { onChunkReceived(loaded); }
          : undefined,
      });
      const fileMetadata = createFileMetadata({ cid, size: bytes.length, sixId, providedMetadata: metadata });

      pocServices.cidDownloadLogger.log(cid, 'stored', { totalBytes: bytes.length });
      dispatch(addFileMetadata(fileMetadata));

      if (onProgress !== undefined) onProgress(PROGRESS_COMPLETE, PROGRESS_COMPLETE);

      return {
        cid,
        metadata: fileMetadata,
        bytesDownloaded: bytes.length,
        source: 'network' as const
      };
    } catch (error) {
      if (signal.aborted) {
        pocServices.cidDownloadLogger.log(cid, 'error', { error: `Timeout after ${maxWaitTime}ms` });
        throw new Error(`Timeout: File ${cid} did not download within ${maxWaitTime}ms`);
      }
      throw error;
    }
  }
);

/**
 * Save a file from Helia to the user's filesystem
 */
export const saveFileToFilesystem = createAppAsyncThunk(
  'helia/saveToFilesystem',
  async(
    {
      sixId,
      cid,
      filename,
      mimeType
    }: {
      sixId: string;
      cid: string;
      filename: string;
      mimeType?: string;
    },
    { extra: { pocServices } }
  ) => {
    const { p2pStackManagerService, fileStoreService, fileDownloadService } = pocServices;

    const helia = p2pStackManagerService.getHeliaNode(sixId);
    if (helia === null) {
      throw new Error('Helia node not initialized');
    }

    const bytes = await fileStoreService.getFile(helia, cid);
    fileDownloadService.saveBytes(bytes, filename, mimeType);

    return { cid, filename, size: bytes.length };
  }
);
